import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    useTheme
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TrackedTextField } from "src/theme/StyledFields";
import ReactMarkdown from "react-markdown";
import { AuthContext } from "../allauth/auth/AuthContext";
import { useAppContext } from "../context/AppContext";
import { getAuth, logout } from "../allauth/lib/allauth";
import ApiClient from "../config/ApiClient";
import { useRoleLabels } from "../hooks/useRoleLabels";

const DEFAULT_WAIVER = "waiver-athlete.md";

interface DigitalConsent {
    email: boolean;
    text: boolean;
    video_call: boolean;
}

type Segment = {
    type: 'markdown' | 'form';
    content?: string;
    formType?: 'digital_consent' | 'media_release' | 'emergency_contact' | 'signature';
};

/** Extracts and validates phone numbers from emergency contact text. Requires at least 2 valid US-style numbers. */
function validateEmergencyContactPhones(text: string): { valid: boolean; count: number; message?: string } {
    const trimmed = (text || "").trim();
    if (!trimmed) {
        return { valid: false, count: 0, message: "Emergency contact information is required" };
    }
    // Match common US/international formats: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890, +1 123 456 7890
    const phoneRegex = /(?:\+?1[-.\s]*)?\(?\d{3}\)?[-.\s]*\d{3}[-.\s]*\d{4}|\b\d{10,11}\b/g;
    const matches = trimmed.match(phoneRegex) || [];
    const normalized = new Set<string>();
    for (const m of matches) {
        const digits = m.replace(/\D/g, "");
        let num = digits;
        if (num.length === 11 && num.startsWith("1")) num = num.slice(1);
        if (num.length === 10) normalized.add(num);
    }
    const count = normalized.size;
    if (count < 2) {
        return {
            valid: false,
            count,
            message: count === 0
                ? "Please provide at least 2 valid phone numbers (e.g., 555-123-4567)"
                : `Please provide at least 2 valid phone numbers (found ${count})`
        };
    }
    return { valid: true, count };
}

export default function WaiverModal() {
    const { auth } = React.useContext<any>(AuthContext) || {};
    const { organization } = useAppContext();
    const { applyRoleLabels } = useRoleLabels();
    const theme = useTheme();

    const waiverName = useMemo(() => {
        const name = organization?.waiver_name || DEFAULT_WAIVER;
        return /^[a-zA-Z0-9_.-]+$/.test(name) ? name : DEFAULT_WAIVER;
    }, [organization?.waiver_name]);

    const user = auth?.data?.user;
    const isAuthenticated = auth?.meta?.is_authenticated === true;
    const open = useMemo(() => Boolean(isAuthenticated && user && user.waiver_signed === false), [isAuthenticated, user]);

    const [markdownContent, setMarkdownContent] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [digitalConsent, setDigitalConsent] = useState<DigitalConsent>({
        email: false,
        text: false,
        video_call: false
    });
    const [mediaReleaseSigned, setMediaReleaseSigned] = useState<boolean>(false);
    const [emergencyContact, setEmergencyContact] = useState<string>("");
    const [signature, setSignature] = useState<string>("");

    // Sync form state with user data when available
    useEffect(() => {
        if (user) {
            setDigitalConsent({
                email: user.digital_consent?.email || false,
                text: user.digital_consent?.text || false,
                video_call: user.digital_consent?.video_call || false
            });
            setMediaReleaseSigned(user.media_release_signed || false);
            setEmergencyContact(user.emergency_contact || "");
        }
    }, [user]);

    // Load markdown content
    useEffect(() => {
        if (!open) return;

        const loadMarkdown = async () => {
            try {
                const response = await fetch(`/waivers/${waiverName}`);
                if (response.ok) {
                    const text = await response.text();
                    setMarkdownContent(text);
                } else {
                    console.error('Failed to load waiver form');
                }
            } catch (error) {
                console.error('Error loading waiver form:', error);
            } finally {
                setLoading(false);
            }
        };

        loadMarkdown();
    }, [open, waiverName]);

    // Parse markdown and split into segments
    const segments = useMemo(() => {
        if (!markdownContent) return [];

        const segments: Segment[] = [];
        const formPlaceholders = [
            { marker: '[FORM:digital_consent]', type: 'digital_consent' as const },
            { marker: '[FORM:media_release]', type: 'media_release' as const },
            { marker: '[FORM:emergency_contact]', type: 'emergency_contact' as const },
            { marker: '[FORM:signature]', type: 'signature' as const }
        ];

        // Find all form placeholders and their positions
        const formPositions: Array<{ index: number; type: Segment['formType']; marker: string }> = [];
        formPlaceholders.forEach(({ marker, type }) => {
            let searchIndex = 0;
            while (true) {
                const index = markdownContent.indexOf(marker, searchIndex);
                if (index === -1) break;
                formPositions.push({ index, type, marker });
                searchIndex = index + marker.length;
            }
        });

        // Sort by position
        formPositions.sort((a, b) => a.index - b.index);

        // Split text at form positions
        let currentIndex = 0;
        formPositions.forEach(({ index, type, marker }) => {
            // Add markdown segment before form
            if (index > currentIndex) {
                const markdownSegment = markdownContent.substring(currentIndex, index);
                if (markdownSegment.trim()) {
                    segments.push({
                        type: 'markdown',
                        content: markdownSegment
                    });
                }
            }

            // Add form segment
            segments.push({
                type: 'form',
                formType: type
            });

            currentIndex = index + marker.length;
        });

        // Add remaining markdown
        if (currentIndex < markdownContent.length) {
            const remainingMarkdown = markdownContent.substring(currentIndex);
            if (remainingMarkdown.trim()) {
                segments.push({
                    type: 'markdown',
                    content: remainingMarkdown
                });
            }
        }

        return segments;
    }, [markdownContent]);

    const emergencyContactValidation = useMemo(
        () => validateEmergencyContactPhones(emergencyContact),
        [emergencyContact]
    );

    const canAccept = useMemo(() => {
        return (
            signature.toUpperCase().trim() === "I AGREE" &&
            digitalConsent.email &&
            emergencyContactValidation.valid
        );
    }, [signature, digitalConsent.email, emergencyContactValidation.valid]);

    const onAccept = useCallback(async () => {
        if (!user?.id) return;
        if (!canAccept) return;

        const payload: any = {
            waiver_signed: true,
            digital_consent: digitalConsent,
            media_release_signed: mediaReleaseSigned,
            emergency_contact: emergencyContact
        };

        const resp = await ApiClient.patch(`/api/users/${user.id}`, payload);
        if (resp.success) {
            try {
                await getAuth(); // refresh session; event updates AuthContext
            } catch (e) {
                // ignore, session refresh will retry on next tick
            }
        }
    }, [user, digitalConsent, mediaReleaseSigned, emergencyContact, signature, canAccept]);

    const onLogout = useCallback(async () => {
        try {
            await logout();
            // The logout function will trigger an auth change event that updates the context
            // The modal will close automatically when user.waiver_signed becomes undefined (user is logged out)
        } catch (e) {
            console.error('Error logging out:', e);
        }
    }, []);

    const renderFormComponent = (formType: Segment['formType']) => {
        switch (formType) {
            case 'digital_consent':
                return (
                    <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalConsent.email}
                                    onChange={(e) => setDigitalConsent({ ...digitalConsent, email: e.target.checked })}
                                    data-attr="waiver-modal_field-consent-email"
                                />
                            }
                            label="Email"
                        />
                        {/* SMS consent hidden for now
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalConsent.text}
                                    onChange={(e) => setDigitalConsent({ ...digitalConsent, text: e.target.checked })}
                                    data-attr="waiver-modal_field-consent-text"
                                />
                            }
                            label="Text Message"
                        />
                        */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalConsent.video_call}
                                    onChange={(e) => setDigitalConsent({ ...digitalConsent, video_call: e.target.checked })}
                                    data-attr="waiver-modal_field-consent-video-call"
                                />
                            }
                            label="Zoom/Video Call"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            (email is required for program communication)
                        </Typography>
                        {!digitalConsent.email && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                Email consent is required to continue.
                            </Typography>
                        )}
                    </Box>
                );

            case 'media_release':
                return (
                    <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <RadioGroup
                            value={mediaReleaseSigned ? 'yes' : 'no'}
                            onChange={(e) => setMediaReleaseSigned(e.target.value === 'yes')}
                            data-attr="waiver-modal_field-media-release"
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio />}
                                label="I give permission for photos, videos, or recordings from sessions to be used for training, marketing, or educational purposes."
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio />}
                                label="I do NOT give permission for any media to be used publicly."
                            />
                        </RadioGroup>
                    </Box>
                );

            case 'emergency_contact':
                return (
                    <Box sx={{ my: 2 }}>
                        <TrackedTextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Emergency Contact Information"
                            placeholder="e.g., Jane Doe 555-123-4567 (Mother), John Doe (555) 987-6543 (Father)"
                            value={emergencyContact}
                            onChange={(e) => setEmergencyContact(e.target.value)}
                            helperText={
                                emergencyContactValidation.valid
                                    ? "Enter at least 2 emergency contacts with names and phone numbers (comma-separated)"
                                    : emergencyContactValidation.message
                            }
                            error={!emergencyContactValidation.valid}
                            data-attr="waiver-modal_field-emergency-contact"
                        />
                    </Box>
                );

            case 'signature':
                return (
                    <Box sx={{ my: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Name:</strong> {user?.first_name || ''} {user?.last_name || ''}
                        </Typography>
                        <TrackedTextField
                            fullWidth
                            required
                            label='Type "I AGREE" to sign'
                            placeholder="I AGREE"
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            helperText={
                                signature.trim() && signature.toUpperCase().trim() !== 'I AGREE'
                                    ? 'You must type "I AGREE" to continue'
                                    : 'Type "I AGREE" to confirm you have read and accept the terms above'
                            }
                            error={signature.trim().length > 0 && signature.toUpperCase().trim() !== 'I AGREE'}
                            sx={{ mb: 2 }}
                            data-attr="waiver-modal_field-signature"
                        />
                        <Typography variant="body2" color="text.secondary">
                            <strong>Date:</strong> {new Date().toLocaleDateString()}
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    if (!open) return null;

    if (loading) {
        return (
            <Dialog open={open} aria-labelledby="waiver-title" maxWidth="md" fullWidth>
                <DialogTitle id="waiver-title">Liability Waiver</DialogTitle>
                <DialogContent>
                    <Typography>Loading waiver form...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            aria-labelledby="waiver-title"
            maxWidth="md"
            fullWidth
            data-testid="waiver-modal"
            PaperProps={{
                sx: {
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle id="waiver-title">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {applyRoleLabels('Guardian Waiver & Consent Form')}
                    </Typography>
                    <Button variant="text" color="primary" onClick={onLogout} data-attr="waiver-modal_logout">Logout</Button>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
                <Box
                    sx={{
                        '& p': { margin: 0 },
                        '& p + p': { marginTop: 2 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: 3,
                            marginBottom: 2,
                            fontWeight: 'bold',
                            color: 'text.primary'
                        },
                        '& ul, & ol': {
                            marginTop: 2,
                            marginBottom: 2,
                            paddingLeft: 3
                        },
                        '& li': {
                            marginBottom: 1
                        },
                        '& hr': {
                            marginTop: 3,
                            marginBottom: 3,
                            borderColor: 'divider'
                        },
                        '& strong': {
                            fontWeight: 'bold'
                        }
                    }}
                >
                    {segments.map((segment, index) => {
                        if (segment.type === 'markdown' && segment.content) {
                            return (
                                <ReactMarkdown key={`markdown-${index}`}>
                                    {segment.content}
                                </ReactMarkdown>
                            );
                        } else if (segment.type === 'form' && segment.formType) {
                            return (
                                <Box key={`form-${index}`}>
                                    {renderFormComponent(segment.formType)}
                                </Box>
                            );
                        }
                        return null;
                    })}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onLogout}
                    data-attr="waiver-modal_logout-footer"
                >
                    Logout
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={onAccept}
                    disabled={!canAccept}
                    data-attr="waiver-modal_accept"
                >
                    I accept
                </Button>
            </DialogActions>
        </Dialog>
    );
}
